import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  IconExpand,
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  useFilters,
  IconTrash,
  ButtonSecondary,
  ButtonSize,
  IconEdit,
  usePrevious,
  Popup,
  PermissionEnum,
  formatDate,
  useGetList,
  ResponseError,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  useLazyGetRiskScoreRangesQuery,
  useDeleteRiskScoreRangesMutation,
  useCreateUncategorizedMutation,
} from './store/services';
import { selectSettings } from './store/slices/list';
import {
  settingsListColumns,
  settingsListSchema,
  settingsListFiltersSchema,
} from './variables/list';
import { RangeType, RiskScoreRange } from '../types';

import ROUTES from '../../routes';

import cn from './risk-levels.module.css';

const formatSettingsList = (list: RiskScoreRange[]): RiskScoreRange[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let action;

    if (item.action === RangeType.NONE) {
      action = 'None';
    } else if (item.action === RangeType.AML_REVIEW) {
      action = 'AML Review';
    } else {
      action = 'OPS Review';
    }

    const render: Record<string, React.JSX.Element> = {
      rangeStart: <div>{`${item.rangeStart} - ${item.rangeEnd}`}</div>,
      action: <div>{action}</div>,
      createdAt: <div>{formatDate(item.createdAt)}</div>,
      updatedAt: (
        <div className={cn.dateUpdated}>
          {formatDate(item.updatedAt)}
          <span>
            Edit
            <IconEdit width={16} height={16} />
          </span>
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const SettingsList = () => {
  const [popupVisible, setPopupVisible] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { values, selectedSettings, pagination, errorsList } = useAppSelector(
    (state) => state.riskLevels.list,
  );

  const { activePermissions } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const { onFilterChange, filters } = useQueryState(settingsListSchema);

  const [getRiskScoreRanges, { isLoading, isFetching }] =
    useLazyGetRiskScoreRangesQuery();
  const [
    deleteRiskScoreRanges,
    { isLoading: deleteInProgress, isSuccess: deleteSuccess },
  ] = useDeleteRiskScoreRangesMutation();
  const [
    createUncategorizedMutation,
    {
      isLoading: createUncategorizedInProgress,
      isSuccess: createUncategorizedSuccess,
    },
  ] = useCreateUncategorizedMutation();

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<RiskScoreRange>({
      getList: getRiskScoreRanges,
      onFilterChange,
      onSortChange: () => {},
      schema: settingsListSchema,
    });

  const prevFilters = usePrevious(filters);

  const formattedData = useMemo(() => {
    const data = formatSettingsList(values as RiskScoreRange[]);

    return data;
  }, [values]);

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: settingsListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedSettings, rows)) {
      dispatch(selectSettings(rows));
    }
  };

  const onDelete = async () => {
    const response = (await deleteRiskScoreRanges({
      ids: selectedSettings,
    })) as { error: ResponseError };

    if (!response.error) {
      if (selectedSettings.length === pagination?.totalCount) {
        createUncategorizedMutation();
      } else {
        getListAction({ filters });
      }
    }
  };

  useScroll({ element: scrollRef, requestInProgress: isFetching });

  useEffect(() => {
    if (prevFilters && !Object.keys(prevFilters).length && filters && !values) {
      getListAction({ filters });
    }
  }, [filters, prevFilters, values]);

  useEffect(() => {
    if (deleteSuccess || createUncategorizedSuccess) {
      setPopupVisible(false);
    }
  }, [deleteSuccess, createUncategorizedSuccess]);

  let content: React.JSX.Element;
  let deleteButtonText = '1 category';
  let popupTitle = 'You want to remove category?';
  let popupDescription = '';
  let popupButtonText = 'Remove category';

  if (isLoading) {
    content = (
      <div className={cn.settingsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={settingsListColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={{}}
        columnsLabels={{}}
        filtersComponent={filtersComponent}
        onFilterChange={(filtersValues) => onFilter(filtersValues)}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        take="30"
        setPage={(data) => setPage({ ...data, filters })}
        tableClassName={!values?.length ? cn.settingsTable : ''}
        pagination={pagination}
        tableBodyClassName={!values?.length ? cn.emptyTable : ''}
        error={errorsList || errorList}
        onRowsSelect={
          activePermissions?.includes(
            PermissionEnum.SETTINGS_RISK_SCORE_RANGES_DELETE_ACTION,
          )
            ? onRowsSelect
            : undefined
        }
        clickable
      />
    );
  }

  if (
    (values?.length as number) !== selectedSettings.length &&
    selectedSettings?.length > 1
  ) {
    popupTitle = `You want to remove ${selectedSettings?.length} categories?`;
    popupButtonText = 'Remove categories';
    deleteButtonText = `${selectedSettings.length} categories`;
  } else if ((values?.length as number) === selectedSettings.length) {
    deleteButtonText = 'all categories';
    popupTitle = 'Please note';
    popupDescription =
      'If you delete a certain risk level(s) and there won’t be any category assigned for this range, it will fall within “Uncategorized” risk level';
    popupButtonText = 'Remove all categories';
  }

  return (
    <>
      <Popup
        title={popupTitle}
        description={popupDescription}
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => onDelete()}
        cancelButtonText="Cancel"
        okButtonText={
          deleteInProgress || createUncategorizedInProgress
            ? 'Deleting...'
            : popupButtonText
        }
        isVisible={popupVisible}
      />
      <div className={cn.settingsBottom} ref={scrollRef}>
        <div className={cn.settingsTopWrapper}>
          <div className={cn.settingsTop}>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_RISK_SCORE_RANGES_CREATE_ACTION,
            ) && (
              <NavLink
                to={`${ROUTES.RISK_LEVELS_CREATE}${search || ''}`}
                className={cn.settingsCreateLink}
              >
                <div className={cn.settingsCreateLinkContent}>
                  <IconExpand className={cn.settingsCreateLinkIcon} />
                  <span>Add category</span>
                </div>
              </NavLink>
            )}

            {selectedSettings.length > 0 && (
              <>
                <div className={cn.settingsTopSeparator} />
                <ButtonSecondary
                  className={`button-white ${cn.deleteSettings}`}
                  size={ButtonSize.SMALL}
                  icon={<IconTrash />}
                  text={`Delete ${deleteButtonText}`}
                  onClick={() => setPopupVisible(true)}
                />
              </>
            )}
          </div>
        </div>
        {content}
      </div>
    </>
  );
};

export default SettingsList;
